import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useTr } from "@/lib/i18n";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [lang, setLang] = useState(() => localStorage.getItem("gg_lang") || "en");
  const [theme, setTheme] = useState(() => localStorage.getItem("gg_theme") || "light");
  const t = useTr(lang);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("gg_theme", theme);
  }, [theme]);
  useEffect(() => { localStorage.setItem("gg_lang", lang); }, [lang]);
  const [onFireQuorum, setOnFireQuorum] = useState(100);
  useEffect(() => { api.get("/settings/public").then(r => setOnFireQuorum(r.data.on_fire_quorum)).catch(() => {}); }, []);

  const checkAuth = useCallback(async () => {
    // Skip auth check if landing from OAuth callback with session_id
    if (window.location.hash?.includes("session_id=")) {
      setCheckingAuth(false);
      return;
    }
    const token = localStorage.getItem("gg_token");
    if (!token) { setCheckingAuth(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch { localStorage.removeItem("gg_token"); }
    setCheckingAuth(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("gg_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (name, email, password, accept_terms = false, age_confirmed = false) => {
    const { data } = await api.post("/auth/register", { name, email, password, accept_terms, age_confirmed });
    return data;
  };
  const verifyEmail = async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    localStorage.setItem("gg_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("server logout failed, clearing local session", err); }
    localStorage.removeItem("gg_token");
    setUser(null);
  };
  const refreshMe = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data.user); return data.user; } catch { return null; }
  };

  return (
    <Ctx.Provider value={{ user, setUser, checkingAuth, lang, setLang, theme, setTheme, t, login, register, verifyEmail, logout, refreshMe, onFireQuorum, setOnFireQuorum }}>
      {children}
    </Ctx.Provider>
  );
}
